import { computed, readonly, ref } from 'vue'
import { useSelectedRowNodes } from '@/Components/DataGrid/Composables/selected-row-nodes'
import { useConfirmDialog } from '@/Composables/confirm-dialog'
import { useErrors } from '@/Composables/errors'
import { settle } from '@/utils/promises'

const FAILED_TO_SUBMIT_BATCH_OPERATION = 'Failed to submit batch operation'

export const useBatchModifier = (gridApi) => {
  const { showError } = useErrors()
  const { show: showConfirmDialog } = useConfirmDialog()
  const { totalSelectedRowNodes } = useSelectedRowNodes({ gridApi: readonly(gridApi) })

  const currentBatchAction = ref(null)
  const submittingBatch = ref(false)
  const submitBatchComplete = ref(() => {})

  const batchActionStart = (action) => {
    currentBatchAction.value = action
    currentBatchAction.value.canSubmit = computed(() => totalSelectedRowNodes.value >= (action.minSelectedRows ?? 1))

    submittingBatch.value = false

    gridApi.value.deselectAll()
    gridApi.value.setColumnsVisible(['checkbox'], true)
    gridApi.value.ensureColumnVisible('checkbox')
  }

  const batchActionStop = () => {
    if (submittingBatch.value) {
      return
    }
    currentBatchAction.value = null
    gridApi.value.deselectAll()
    gridApi.value.setColumnsVisible(['checkbox'], false)
  }

  const batchActionSubmit = async () => {
    if (currentBatchAction.value.requiresConfirmation) {
      const [cancelled] = await settle(
        showConfirmDialog({
          type: 'danger',
          title: 'Please Confirm',
          body: currentBatchAction.value.actionDescription ?? 'Are you sure you want to continue?'
        })
      )
      if (cancelled) {
        return
      }
    }

    const mapper = currentBatchAction.value.actionMapper ?? ((node) => node.data.id)

    const selectedIds = [...new Set(gridApi.value.getSelectedNodes().map(mapper))]

    submittingBatch.value = true
    const [err, _] = await settle(currentBatchAction.value.actionFn(selectedIds))
    submittingBatch.value = false

    if (err) {
      showError({ message: err.message ?? FAILED_TO_SUBMIT_BATCH_OPERATION, useToast: true })
    }

    batchActionStop()
    submitBatchComplete.value()
  }

  const onSubmitBatch = (fn) => {
    submitBatchComplete.value = fn
  }

  return {
    currentBatchAction,
    submittingBatch,

    batchActionStart,
    batchActionStop,
    batchActionSubmit,

    onSubmitBatch
  }
}
